import React, { SyntheticEvent } from 'react'
import clsx from 'clsx'
import uniqueId from 'lodash.uniqueid'
import Dayzed, { DateObj, RenderProps } from 'dayzed'
import { addDays, addMonths, differenceInDays, isBefore } from 'date-fns'
import { motion } from 'framer-motion'
import { Calendar as CalendarStyles } from '@airbus/elements/elements'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { createUseStyles } from '../../lib/styling'
import useControlled from '../../lib/hooks/useControlled'
import {
  getMinYear,
  getMonthNames,
  getTimeWithoutPrecision,
  getWeekdayNames,
  getYearRange,
  isCurrentMonth,
} from '../../lib/utils'
import IconButton from '../IconButton'
import { CommonProps } from '../helperTypes'
import Select, { SelectOption } from '../Select'

type Props = {
  /** default selected date(s) */
  defaultValue?: Date | Date[]
  /**
   * First day of the week with possible values 0-6 (Sunday to Saturday).
   * @default 0
   */
  firstDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  /**
   * Format the name of week day in calendar (short: one letter, long: three letters)
   * @default long
   */
  formatWeekDay?: 'short' | 'long'
  /**
   * Defines which locale should be used by the date picker and the calendar
   * @default en-GB
   */
  locale?: string
  /** Defines minimum date that the user can select */
  minDate?: Date
  /** Defines maximum date that the user can select */
  maxDate?: Date
  /**
   * Date selection mode
   * @default single
   */
  mode?: 'single' | 'multi' | 'range'
  /**
   * the number of month to display
   * @default 1
   */
  monthCount?: number
  /** date select callback */
  onChange?: (date: Date | Date[]) => void
  /**
   * Defines whether days from previous or next month shall be rendered if the month doesn't start on the first day of the week or doesn't end on the last day of the week, respectively
   * @default true
   */
  showOutsideDays?: boolean
  /** selected date(s) */
  value?: string | Date | Date[]
  /**
   * Range of years allowed for selection.<br />
   * Ignored if both minDate and maxDate are provided.<br />
   * If only minDate or maxDate are provided the range will start from the one defined.
   * @default 100
   */
  yearRange?: number
}

export type CalendarProps = Props & CommonProps

export type CalendarRef = {
  focus: (trap?: boolean) => void
  getOffset: () => number
  setOffset: (offset: number) => void
}

const useStyles = createUseStyles(CalendarStyles, 'calendar')

const isInRange = (date: Date, value: Date | Date[], hoveredDate?: Date) => {
  if (!date) return false
  if (!Array.isArray(value)) return false
  if (!value.length || value.length > 2) return false

  const dateValue = getTimeWithoutPrecision(date)
  const firstSelected = getTimeWithoutPrecision(value[0])
  const secondSelected = getTimeWithoutPrecision(value[1])
  if (hoveredDate) {
    const hovered = getTimeWithoutPrecision(hoveredDate)
    return firstSelected < dateValue && hovered >= dateValue
    // return (
    //   (firstSelected < dateValue && hovered >= dateValue) ||
    //   (dateValue < firstSelected && dateValue >= hovered)
    // );
  }
  return firstSelected < dateValue && secondSelected > dateValue
}

const checkValue = (value: Date | Date[], mode: string): void => {
  if (!value) return
  if (mode === 'single') {
    if (Array.isArray(value)) {
      throw new Error("Calendar 'value' must be a Date object when used in 'single' mode.")
    }
  } else if (mode === 'range') {
    if (!Array.isArray(value) || value.length > 2) {
      throw new Error(`Calendar 'value' must be an array of one or two Date objects when used in 'range' mode.`)
    }
  } else {
    if (!Array.isArray(value)) {
      throw new Error(`Calendar 'value' must be an array of Date objects when used in 'multi' mode.`)
    }
  }
}

export const Calendar = (props: CalendarProps, ref: React.Ref<CalendarRef>) => {
  const {
    className,
    defaultValue,
    firstDayOfWeek = 0,
    formatWeekDay = 'long',
    locale = 'en-GB',
    minDate,
    maxDate,
    mode = 'single',
    monthCount = 1,
    onChange,
    showOutsideDays = true,
    value: valueProp,
    yearRange: yearRangeProp = 100,
    ...rest
  } = props
  const classes = useStyles()

  /**
   * Years are calulcated as follows
   * Default is 100
   * Default minYear is current year minus half of yearRange
   * Default maxYear is current year plus half of yearRange
   * if minDate and maxDate given yearRange is ignored
   * if either minDate or maxDate is only given then given year and yearRange considered for range
   */
  const yearRange = getYearRange(yearRangeProp, minDate, maxDate)
  const minYear = React.useMemo(() => getMinYear(yearRange, minDate, maxDate), [minDate, maxDate, yearRange])
  const years: Array<SelectOption> = React.useMemo(
    () =>
      Array.from({ length: yearRange + 1 }, (_, i) => i).map((i) => {
        const label = `${minYear + i}`
        return { label, value: label }
      }),
    [minYear, yearRange],
  )

  // Implement CalendarRef API
  React.useImperativeHandle(ref, () => ({
    focus: () => {
      if (!focusableRef.current) {
        const focusableDays = getDays()
        if (focusableDays.length > 0) focusableDays[0].focus()
        return
      }
      focusableRef.current!.focus()
    },
    getOffset: () => offset,
    setOffset,
    // FIXME API to set current edited date from the outside
    // setRangePosition(pos: number) {
    //   if (mode !== "range") return;
    //   console.log("[Calendar] setRangePosition", pos);
    //   setEditionPosition(pos);
    // },
  }))
  const localRef = React.useRef<HTMLDivElement>(null)
  const focusableRef = React.useRef<HTMLButtonElement>(null)

  // controlled / uncontrolled value
  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue: defaultValue,
  })
  React.useMemo(() => checkValue(value, mode), [value, mode])
  React.useEffect(() => setOffset(0), [value])

  // week and month names
  const weekdayNames: Array<String> = React.useMemo(
    () => getWeekdayNames(locale, formatWeekDay, firstDayOfWeek),
    [firstDayOfWeek, formatWeekDay, locale],
  )
  const monthNames: Array<String> = React.useMemo(() => getMonthNames(locale), [locale])
  const monthNameValuePairs: Array<SelectOption> = React.useMemo(() => {
    let result = getMonthNames(locale).map((monthName, i) => ({
      label: String(monthName),
      value: i + '',
    }))
    // reduce months list if minDate & maxDate are defined in the same year
    if (minDate && maxDate && years.length === 1) {
      const firstMonth = minDate.getMonth()
      const lastMonth = maxDate.getMonth()
      result = result.slice(firstMonth, Math.max(lastMonth, firstMonth + 1))
    }
    return result
  }, [locale, minDate, maxDate, years])

  const getDays = React.useCallback((): HTMLButtonElement[] => {
    if (!localRef.current) return []
    return Array.from(localRef.current.querySelectorAll("button[class*='day']:not([class$='outside']):not([disabled])"))
  }, [])

  // hovered day for range selection
  const [hoveredDate, setHoveredDate] = React.useState<Date | undefined>()

  // month navigation offset
  const [offset, setOffset] = React.useState(0)
  const [direction, setDirection] = React.useState(0)
  const handleOffsetChanged = React.useCallback(
    (newOffset: number) => {
      setDirection(newOffset > offset ? 1 : -1)
      setOffset(newOffset)
    },
    [offset],
  )

  const handleChange = (dateObj: DateObj, evt: SyntheticEvent) => {
    evt.preventDefault()
    const val = dateObj.date
    let newValue: Date | Date[] = val
    if (mode === 'range') {
      if (value && value.length && value.length === 1) {
        if (isBefore(value[0], val) || differenceInDays(value[0], val) === 0) {
          val.setHours(23)
          val.setMinutes(59)
          val.setSeconds(59)
          val.setMilliseconds(999)
          newValue = [value[0], val]
        } else {
          newValue = [val]
        }
      } else {
        newValue = [val]
      }
    } else if (mode === 'multi') {
      if (value) {
        const valIndex = value.findIndex((d: Date) => d.getTime() === val.getTime())
        if (valIndex >= 0) {
          newValue = [...value]
          newValue.splice(valIndex, 1)
        } else {
          newValue = [...value, val]
        }
      } else {
        newValue = [val]
      }
    }
    setHoveredDate(undefined)
    // setEditionPosition(undefined);
    setValue(newValue)
    setOffset(0)
    if (dateObj.nextMonth) setDirection(1)
    else if (dateObj.prevMonth) setDirection(-1)
    else setDirection(0)
    onChange && onChange(newValue)
  }

  // mouse handlers for range selection
  const handleMouseLeave = () => {
    setHoveredDate(undefined)
  }
  const handleMouseEnterDay = (date: Date) => {
    if (!(value && value.length) || value.length === 2) return
    setHoveredDate(date)
  }

  // keyboard navigation
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const managedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageDown', 'PageUp']
      const { key } = event.nativeEvent
      const focusedDay = document.activeElement
      if (!managedKeys.includes(key)) return
      if (!localRef.current) return
      if (!focusedDay || focusedDay.className.indexOf('-day') < 0) return
      event.preventDefault()

      const focusedDate: Date = new Date(focusedDay.getAttribute('aria-label') as string)
      let dayOffset = 0
      let newFocusedDate: Date
      switch (key) {
        default:
        case 'ArrowLeft':
          newFocusedDate = addDays(focusedDate, -1)
          dayOffset = -1
          break
        case 'ArrowRight':
          newFocusedDate = addDays(focusedDate, 1)
          dayOffset = 1
          break
        case 'ArrowUp':
          newFocusedDate = addDays(focusedDate, -7)
          dayOffset = -7
          break
        case 'ArrowDown':
          newFocusedDate = addDays(focusedDate, 7)
          dayOffset = 7
          break
        case 'PageUp':
          newFocusedDate = addMonths(focusedDate, -1)
          dayOffset = differenceInDays(newFocusedDate, focusedDate)
          break
        case 'PageDown':
          newFocusedDate = addMonths(focusedDate, 1)
          dayOffset = differenceInDays(newFocusedDate, focusedDate)
          break
      }

      const focusDayByDate = (days: HTMLButtonElement[], date: Date) => {
        const day: HTMLButtonElement | undefined = days.find(
          (day) => day.getAttribute('aria-label') === date.toDateString(),
        )
        if (day) day.focus()
      }

      const focusableDays = getDays()
      focusableDays.forEach((button: HTMLButtonElement, i: number) => {
        const newNodeKey = i + dayOffset
        if (button === focusedDay) {
          if (newNodeKey < 0) {
            if (minDate && isBefore(newFocusedDate, minDate)) return
            handleOffsetChanged(offset - monthCount)
            setTimeout(() => {
              focusDayByDate(getDays(), newFocusedDate)
            })
          } else if (newNodeKey >= focusableDays.length) {
            if (maxDate && isBefore(maxDate, newFocusedDate)) return
            handleOffsetChanged(offset + monthCount)
            setTimeout(() => {
              focusDayByDate(getDays(), newFocusedDate)
            })
          } else {
            focusableDays[newNodeKey].focus()
          }
        }
      })
    },
    [getDays, handleOffsetChanged, minDate, maxDate, monthCount, offset],
  )

  const renderCalendar = ({ calendars, getBackProps, getForwardProps, getDateProps }: RenderProps) => {
    if (!calendars.length) return null
    // disable back and forward months buttons if needed
    const startMonth = calendars[0].month
    const endMonth = calendars[calendars.length - 1].month
    const year = calendars[0].year
    const minMonth = minDate ? minDate.getMonth() : 0
    const maxMonth = maxDate ? maxDate.getMonth() : 11
    const disableBackBtn = year === minYear && startMonth === minMonth
    const disableFwdBtn = year === minYear + yearRange && endMonth === maxMonth

    return (
      <div
        className={clsx(className, classes.root)}
        onKeyDownCapture={handleKeyDown}
        onMouseLeave={handleMouseLeave}
        ref={localRef}
        {...rest}
      >
        <div className={classes.navbar}>
          <IconButton variant='ghost' size='small' disabled={disableBackBtn} {...getBackProps({ calendars })}>
            <ChevronLeft />
          </IconButton>
          <Select
            className={classes.navbar_select}
            aria-label='Select month'
            disabled={monthNameValuePairs.length === 1}
            options={monthNameValuePairs}
            value={calendars[0].month + ''}
            cbProps={{ hideClear: true }}
            onChange={(event, selMonth) => setOffset(offset + parseInt(selMonth) - calendars[0].month)}
          />
          <Select
            className={classes.navbar_select}
            aria-label='Select year'
            disabled={years.length === 1}
            options={years}
            value={calendars[0].year + ''}
            cbProps={{ hideClear: true }}
            onChange={(event, selYear) => setOffset(offset + (parseInt(selYear) - calendars[0].year) * 12)}
          />
          <IconButton variant='ghost' size='small' disabled={disableFwdBtn} {...getForwardProps({ calendars })}>
            <ChevronRight />
          </IconButton>
        </div>
        <div className={classes.months} ref={ref as React.Ref<HTMLDivElement>}>
          {calendars.map((calendar, idx) => (
            <motion.div
              initial={{
                opacity: direction !== 0 ? 0 : 1,
                x: direction * 100,
              }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.1 }}
              key={`${calendar.month}${calendar.year}${idx}`}
              className={clsx(classes.month, isCurrentMonth(calendar.year, calendar.month) && classes.month_today)}
            >
              <div className={classes.caption}>{idx > 0 && `${monthNames[calendar.month]} ${calendar.year}`}</div>
              {weekdayNames.map((weekday) => (
                <div key={uniqueId(`${calendar.month}${calendar.year}${weekday}`)} className={classes.weekday}>
                  {weekday}
                </div>
              ))}
              {calendar.weeks.map((week, weekIndex) =>
                week.map((dateObj, index) => {
                  let key = `${calendar.month}${calendar.year}${weekIndex}${index}`
                  if (!dateObj) {
                    return <button type='button' disabled key={key} className={classes.day} tabIndex={-1}></button>
                  }

                  let { date, selected, selectable, today, prevMonth, nextMonth } = dateObj
                  const inRange = isInRange(date, value, hoveredDate)
                  const focusable = selected || (!value && today)
                  return (
                    <button
                      type='button'
                      className={clsx(
                        classes.day,
                        today && classes.day_today,
                        selected && !inRange && classes.day_selected,
                        mode === 'range' && inRange && classes.day_range,
                        !selectable && classes.day_outside,
                        (prevMonth || nextMonth) && classes.day_outside,
                      )}
                      key={key}
                      ref={focusable ? focusableRef : undefined}
                      tabIndex={focusable ? 0 : -1}
                      {...getDateProps({
                        dateObj,
                        onFocus: () => handleMouseEnterDay(date),
                        onMouseEnter: () => handleMouseEnterDay(date),
                      })}
                    >
                      {date.getDate()}
                    </button>
                  )
                }),
              )}
            </motion.div>
          ))}
        </div>
      </div>
    )
  }

  // set initialDate to undefined when the controlled value is ""
  // so that Dayzed will default to current today date as initialDate
  const controlledValue = value === '' ? undefined : value
  const initialDate = mode === 'single' ? controlledValue : controlledValue && controlledValue[0]

  return (
    <Dayzed
      date={initialDate}
      offset={offset}
      firstDayOfWeek={firstDayOfWeek}
      minDate={minDate}
      maxDate={maxDate}
      monthsToDisplay={monthCount}
      onDateSelected={handleChange}
      onOffsetChanged={handleOffsetChanged}
      render={renderCalendar}
      selected={controlledValue}
      showOutsideDays={showOutsideDays}
    />
  )
}

export default React.forwardRef<CalendarRef, CalendarProps>(Calendar)
